import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { isDniFeatureEnabled } from '@/config/featureFlags';
import { URL_ROUTES } from '@/constants';
import {
  sendIdentifyPageViewGTMEvent,
  sendPageVirtual
} from '@/modules/shared/utils/gtmEvents/events/customerDataEvents';
import {
  getWeddingID,
  isWeddingFlow
} from '@/modules/shared/utils/isWeddingFlow';
import { CustomerDataSummary } from '@/Pages/CustomerDataPage/CustomerDataSummary.tsx';
import useCustomerDataPageInitializer from '@/Pages/CustomerDataPage/useCustomerDataPageInitializer';
import useCartPayments from '@/sections/cart/hooks/useCartPayments';
import CustomerDataMain from '@/sections/customerData/CustomerDataMain';
import { getAddressGuestInitialData } from '@/sections/customerData/mappers/addressGuestToAddressForm';
import { mapShippingAddressToFormValues } from '@/sections/customerData/mappers/shippingAddressToCustomerForm';
import type { AddressGuestFormValues } from '@/sections/customerData/types/AddressGuestFormValues';
import { addressGuestValidator } from '@/sections/customerData/validators/addressGuestValidator';
import { LocalHookFormDevtool } from '@/sections/shared/hooks/local-hookform-devtool';

const isWedding = isWeddingFlow();

const CustomerDataPage = () => {
  useCustomerDataPageInitializer();
  const navigate = useNavigate();
  const cart = useAppSelector((state) => state?.cart?.data);
  useCartPayments(cart);

  const isDniEnabled = isDniFeatureEnabled();
  const shippingAddress = cart?.shippingAddress;
  const customerDataFromShippingAddress = mapShippingAddressToFormValues(
    shippingAddress,
    isDniEnabled
  );
  const addressFormInitialValues = getAddressGuestInitialData(
    customerDataFromShippingAddress
  );
  const lineItems = cart?.lineItems;

  const customerForm = useForm<AddressGuestFormValues>({
    resolver: yupResolver(addressGuestValidator),
    values: addressFormInitialValues,
    defaultValues: addressFormInitialValues
  });

  const redirectToWeddingBilling = useCallback(() => {
    const weddingId = getWeddingID();
    const billingPath = URL_ROUTES.WEDDING_BILLING_PATH;
    const url = billingPath?.replace(':weddingId', weddingId);
    return navigate(url);
  }, [navigate]);

  useEffect(() => {
    const isLogged = cart?.customerId;
    if (isLogged && isWedding) {
      return redirectToWeddingBilling();
    }
    if (isLogged) {
      return navigate(URL_ROUTES.SHIPPING);
    }
  }, [cart, navigate, redirectToWeddingBilling]);

  useEffect(() => {
    if (lineItems && lineItems.length > 0) {
      sendIdentifyPageViewGTMEvent(lineItems);
    }
  }, [lineItems]);

  useEffect(() => {
    sendPageVirtual();
  }, []);

  return (
    <main className="co-max-w-[1536px] co-w-screen co-grid co-grid-cols-1 tablet_w:co-grid-cols-[1fr_375px] desktop:co-grid-cols-[1fr_430px]">
      <FormProvider {...customerForm}>
        <CustomerDataMain />
        <CustomerDataSummary />
        <LocalHookFormDevtool control={customerForm?.control} />
      </FormProvider>
    </main>
  );
};

export default CustomerDataPage;
